import Table, { dateFormatter } from "../../../components/tables/table";
import { getNonEmpty } from '../../../../utils/helpers'
import { useEffect, useState } from "react";
import { alertError, alertSuccess } from "../../../../utils/logger";
import { getAllActiveSubPlans } from "../../../../api/subPlan";
import { Dialog, DialogActions, DialogContent, DialogTitle, TextField, Button } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { DefaultTextField } from "./EditCustomerPage";
import { postPlanCustomer } from "../../../../api/planCustomer";

const SPAIN_COUNTRY_ID = "6a294d93c011405fb4819ca7";
const SPAIN_VAT_FACTOR = 1.21;

function isSpainCountryRef(countryRef) {
    if (!countryRef) return false;
    if (typeof countryRef === "string") return countryRef === SPAIN_COUNTRY_ID;
    if (typeof countryRef === "object" && countryRef._id) return String(countryRef._id) === SPAIN_COUNTRY_ID;
    return String(countryRef) === SPAIN_COUNTRY_ID;
}

function roundMoney(value) {
    return Math.round(value * 100) / 100;
}

function displayPriceWithVatIfSpain(amount, isSpain) {
    const n = Number(amount);
    if (!Number.isFinite(n)) return null;
    if (!isSpain || n <= 0) return n;
    return roundMoney(n * SPAIN_VAT_FACTOR);
}

function planDialog(activePlans, isNew, planOpen, setPlanOpen, setPlans, setTableKeyUsers, selectedPlan, setSelectedPlan, customerId) {
    const dialogTitle = isNew ? "Add new plan" : "Change plan";


    function saveUser() {
        setPlans([selectedPlan.plan])
        setTableKeyUsers(k => k + 1);
        setPlanOpen(false)
    }

    return (
        <Dialog
            open={planOpen}
            onClose={() => setPlanOpen(false)}
            aria-labelledby="confirm-dialog"
            PaperProps={{
                style: {
                    width: '50vw',
                    height: '50vh',
                    maxWidth: 'none'
                }
            }}>
            <DialogTitle id="confirm-dialog">{dialogTitle}</DialogTitle>
            <DialogContent style={{ overflowX: 'hidden' }}>


                <div className="d-flex align-item-center">
                    <div className="col-12">
                        <Autocomplete
                            id='AdvServices'
                            options={activePlans}
                            value={selectedPlan?.plan || null}
                            getOptionLabel={(option) => [getNonEmpty(option.title), option.target?.toLowerCase()].join(" ")}
                            onChange={(_, newValue) => {
                                setSelectedPlan({
                                    ...selectedPlan,
                                    plan: newValue
                                })
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label='Customer plans'
                                    margin="normal"
                                    variant="outlined"
                                />
                            )}
                        />
                    </div>

                </div>
                <div className="d-flex align-item-center">
                    {selectedPlan?.plan?.monthPrice > 0 &&
                        <div className="col-6">
                            <DefaultTextField label={"Months"} value={selectedPlan?.months}
                                onChange={(e) => setSelectedPlan({ ...selectedPlan, months: e.target.value })} type="number" />
                        </div>
                    }
                    {!isNew && selectedPlan?.plan &&
                        <div className="col-6">
                            <DefaultTextField label={"Start date"} value={selectedPlan?.backofficeStartDate}
                                onChange={(e) => setSelectedPlan({ ...selectedPlan, backofficeStartDate: e.target.value })} type="date" />
                        </div>
                    }

                </div>
            </DialogContent>
            <DialogActions>
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                        if (isNew) {
                            saveUser()
                        }
                        else {
                            const planCustomer = {
                                planId: selectedPlan?.plan?._id,
                                months: Number(selectedPlan?.months),
                                customerId: customerId,
                                backofficeStartDate: selectedPlan?.backofficeStartDate
                            };

                            postPlanCustomer(planCustomer)
                                .then((res) => {
                                    const actualPlan = {
                                        ...res.data.plan,
                                        startDate: res.data.startDate,
                                        endDate: res.data.endDate
                                    }
                                    const history = res.data.history.map(pcH => ({
                                        ...pcH.plan,
                                        startDate: pcH.startDate,
                                        endDate: pcH.endDate
                                    }))

                                    setPlans([actualPlan, ...history.slice().reverse()])
                                    setTableKeyUsers(k => k + 1);
                                    setPlanOpen(false)
                                    alertSuccess({
                                        title: "Changed!",
                                        customMessage: "Customer plan changed successfully.",
                                    });
                                })
                        }
                    }}
                >
                    Save
                </Button>
                <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                        setPlanOpen(false)
                        setSelectedPlan(null)
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default function SubscriptionPlanSection({ plans, setPlans, customer, selectedPlan, setSelectedPlan, rolePathSingle }) {
    const [activePlans, setActivePlans] = useState([]);

    const [tableKeyUsers, setTableKeyUsers] = useState(0);
    const [isNew, setIsNew] = useState(false);
    const [planOpen, setPlanOpen] = useState(false);

    const today = new Date().toISOString().split("T")[0];


    useEffect(() => {
        getAllActiveSubPlans()
            .then((res) => { setActivePlans(res.data.filter((option) => option.target?.toLowerCase() === rolePathSingle)) })
            .catch((error) => { alertError({ error: error, customMessage: "Could not get advisor services." }); })
    }, [rolePathSingle])


    const isSpain = isSpainCountryRef(customer?.country);

    const priceFormatter = (cell) => {
        const value = displayPriceWithVatIfSpain(cell, isSpain);
        return (
            <div style={{ textAlign: 'right', width: '30%' }}>
                {value != null ? `${value} €` : '-'}
            </div>
        );
    };

    const columns = [
        { dataField: "title", text: "Plan name", formatter: (cell) => getNonEmpty(cell), sort: true },
        { dataField: "monthPrice", text: "Monthly price", sort: true, formatter: priceFormatter },
        { dataField: "yearPrice", text: "Annual price", sort: true, formatter: priceFormatter },
        {
            dataField: "startDate",
            text: "Start date",
            formatter: dateFormatter,
            sort: true,
        },
        {
            dataField: "endDate",
            text: "End date",
            formatter: dateFormatter,
            sort: true,
        },
        { dataField: "_id", text: "", formatter: (cell) => "", headerStyle: { width: '50px' } }
    ];

    return (
        <>
            {!customer?._id && !selectedPlan &&
                <div className="d-flex justify-content-end col-12">
                    <button
                        type="button"
                        className="btn btn-primary text-white"
                        onClick={() => {
                            setPlanOpen(true)
                            setIsNew(true)
                            setSelectedPlan({ backofficeStartDate: today })
                        }}
                    >
                        Add new
                    </button>
                </div>
            }
            {customer?._id &&
                <div className="d-flex justify-content-end col-12">
                    <button
                        type="button"
                        className="btn btn-primary text-white"
                        onClick={() => {
                            setPlanOpen(true)
                            setSelectedPlan({ backofficeStartDate: today })
                        }}
                    >
                        Change plan
                    </button>
                </div>
            }
            <div className="mt-10" />
            <Table data={plans || []} columns={columns} key={tableKeyUsers} />

            {planDialog(activePlans, isNew, planOpen, setPlanOpen, setPlans, setTableKeyUsers, selectedPlan, setSelectedPlan, customer?._id)}

        </>
    );
}